import {isValidOutput} from "./blockValidation";
import checkIfBlockIsVisible from "./checkIfBlockIsVisible";
import convertRecursiveRows from "./convertRecursiveRows";
import getRecursiveOutput from "./getRecursiveOutput";
import retrieveKCCOptimizationResponses from "./retrieveKCCOptimizationResponses";
import getKccResultContextKey from "./getKccResultContextKey";
import evaluateJsonOutputValue from "./evaluateJsonOutputValue";
import evaluateRecursiveSumOutput from "./evaluateRecursiveSumOutput";
import getKColorResultContextKey from "./getKColorResultContextKey";
import retrieveKColorResponses from "./retrieveKColorResponses";
import getExpressionConfiguration from "./getExpressionConfiguration";

const evaluateOutputCalculation = (
  output,
  context,
  originalOutputField=undefined,
  recursiveResponsesIndex=undefined,
  skipContextCheck=false,
) => {
  const { calculation, output_currency, output_ceil } = output;

  const finalCalculation = getExpressionConfiguration(output, context, 'calculation');

  const { responses, products, configuration } = context;
  const result = finalCalculation
    .map((v) => {
      if (v[0] === "{") {
        const {
          inputId,
          value,
          outputId,
          outputType,
          column,
          defaultValue,
          product,
          original,
          field,
        } = JSON.parse(v);

        // --- CASE A: output is a kcc optimization
        if (original === 'kcc_optimization') {
          const kccResponses = retrieveKCCOptimizationResponses(output, responses, context);
          const kccResultContextKey = getKccResultContextKey(kccResponses);
          const kccResult = context?.kccOptimizationResults?.[kccResultContextKey];

          return kccResult?.value?.reduce((acc, row) => acc + row.entry_total_price, 0) || defaultValue || 0;
        }

        // --- CASE B: output is a spreadsheet
        if (original === 'spreadsheet') {
          if (originalOutputField === undefined) {
            return null;
          }
          if (recursiveResponsesIndex === undefined) {
            return context.spreadsheetResults?.[output.id]?.reduce((acc, row) => acc + row[originalOutputField], 0) || 0;
          }
          return context.spreadsheetResults?.[output.id]?.[recursiveResponsesIndex]?.[originalOutputField]
            || 0;
        }

        // --- CASE C: output is a kcolor computation
        if (original === 'kcolor') {
          const {product, color, productPackage} = retrieveKColorResponses(responses, output);
          const key = getKColorResultContextKey(product, color, productPackage);
          const result = context?.kColorResults?.[key];
          switch(originalOutputField) {
            case "price":
              const priceChosen =
                context?.globalTargetPrice
                || context?.kColorPricesChosen?.[key]?.[output.id]?.chosenPrice
                || "kerakoll";
              if (!result) {
                return 0;
              }
              return [null, undefined, "kerakoll"].includes(priceChosen) ?
                result?.price?.kerakoll :
                // if tintometer price is not available, fallback to kerakoll.
                // only a few roles can actually see tintometer prices, but agents (who can't see it) when
                // creating a quotation may need to get the value of the outputs containing the products in order to
                //  create the quotation and see the product quantity in the quotation.
                result?.price?.tintometer ? result?.price?.tintometer : result?.price?.kerakoll;
            case "resa":
              return result?.resa || 0;
            case "min_qty":
              return result?.min_qty || 0;
            default:
              return defaultValue || 0;
          }
        }

        // --- CASE D: output is a recursive sum
        if (original === 'recursive_sum') {
          return evaluateRecursiveSumOutput(v, context, originalOutputField, skipContextCheck);
        }

        // --- CASE E: output is a generic output
        if (original === 'connector') {
          const type = output.metadata_?.connector_type || "diaphragm";
          const connectorResults = context.connectionResults?.[type];
          return !!connectorResults && !!originalOutputField
            ? retrieveObjectPathValue(connectorResults, originalOutputField)
            : undefined;
        }

        // --- CASE F: output is a generic output
        switch (outputType) {
          case "recursive_question": {
            if (!responses[inputId]) {
              return defaultValue;
            }
            const { recursive_question_value, question } = responses[inputId];
            if (
              !recursive_question_value?.length ||
              !question?.blocks?.length
            ) {
              return defaultValue;
            }

            const lastOutput = question.blocks.reduce(
              (acc, block) => (isValidOutput(block) ? block : acc),
              null
            );

            if (!lastOutput) {
              return defaultValue;
            }

            const rows = convertRecursiveRows(
              recursive_question_value,
              question.blocks.filter((b) => isValidOutput(b)),
              null,
              context
            );

            return getRecursiveOutput(lastOutput, rows);
          }
          case "number":
            return value;
          case "output": {
            if (!outputId) {
              return defaultValue || 0;
            }

            const calculationOutput = configuration.find(
              (d) => d.id === outputId
            );

            if (!calculationOutput) {
              return defaultValue || 0;
            }

            if (!checkIfBlockIsVisible(calculationOutput, context, recursiveResponsesIndex, skipContextCheck)) {
              return defaultValue || 0;
            }

            if (calculationOutput.output_type === 'json_output_value'
              && calculationOutput.metadata_?.path) {
              return evaluateJsonOutputValue(calculationOutput, context, recursiveResponsesIndex);
            }

            return evaluateOutputCalculation(calculationOutput, context, originalOutputField || field, recursiveResponsesIndex, skipContextCheck);
          }

          case "number_question": {
            if (responses[inputId]?.hidden || !responses[inputId]?.value) {
              return defaultValue;
            }

            // custom behavior: connector input (custom nested component)
            const question = responses[inputId]?.question;
            if (question.input_type === "connector_input")
              return responses[inputId]?.value?.[field] || defaultValue;

            if (responses[inputId]?.option?.option_value) {
              return responses[inputId]?.option?.option_value;
            }
            return responses[inputId].value;
          }

          case "product_question": {
            if (column === "value") {
              return responses[inputId]?.option?.option_value || defaultValue;
            }

            return (
              responses[inputId]?.option?.product?.[column] || defaultValue
            );
          }

          case "product": {
            return products?.[product?.value]?.[column] || defaultValue;
          }

          default:
            return defaultValue || 1;
        }
      }

      return v;
    })
    .join(" ");

  if (!result) {
    return false;
  }

  try {
    // console.log(result, output.name);
    // eslint-disable-next-line
    const number = eval(result);

    if (isNaN(number)) {
      return 0;
    }

    if (!output_currency) {
      if (output_ceil) {
        return Math.ceil(number)
      }
      return Math.round(number * 100) / 100;
    }

    return number;
  } catch (error) {
    console.log(error);
    return false;
  }
};

export default evaluateOutputCalculation;
